/**
 * @fileoverview Lista das triagens cadastradas: situação, versão publicada,
 * triagens em andamento e ações (editar, duplicar, arquivar, restaurar).
 */

import React, { useCallback, useEffect, useState } from "react";
import CircularProgress from "@material-ui/core/CircularProgress";
import Checkbox from "@material-ui/core/Checkbox";
import Tooltip from "@material-ui/core/Tooltip";
import { Archive, ArchiveRestore, Copy, Pencil, Plus } from "lucide-react";
import { toast } from "react-toastify";

import api from "../../../../services/api";
import toastError from "../../../../errors/toastError";
import { i18n } from "../../../../translate/i18n";
import ConfirmationModal from "../../../../components/ConfirmationModal";
import useStyles from "./styles";

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString(i18n.language?.startsWith("en") ? "en-US" : i18n.language?.startsWith("es") ? "es-ES" : "pt-BR");
};

const DefinitionList = ({ onEdit, onNew }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [definitions, setDefinitions] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [archiving, setArchiving] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/triage-definitions");
      setDefinitions(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const duplicate = async (def) => {
    setBusyId(def.id);
    try {
      const { data: full } = await api.get(`/triage-definitions/${def.id}`);
      await api.post("/triage-definitions", {
        name: i18n.t("triage.manager.copyName", { name: full.name }).slice(0, 120),
        description: full.description,
        config: full.config,
      });
      toast.success(i18n.t("triage.manager.duplicated"));
      await load();
    } catch (err) {
      toastError(err);
    } finally {
      setBusyId(null);
    }
  };

  const archive = async () => {
    const def = archiving;
    setBusyId(def.id);
    try {
      await api.post(`/triage-definitions/${def.id}/archive`);
      toast.success(i18n.t("triage.manager.archived"));
      setArchiving(null);
      await load();
    } catch (err) {
      toastError(err);
    } finally {
      setBusyId(null);
    }
  };

  const restore = async (def) => {
    setBusyId(def.id);
    try {
      await api.post(`/triage-definitions/${def.id}/restore`);
      toast.success(i18n.t("triage.manager.restored"));
      await load();
    } catch (err) {
      toastError(err);
    } finally {
      setBusyId(null);
    }
  };

  const visible = definitions.filter((d) => showArchived || d.status !== "archived");
  const hasArchived = definitions.some((d) => d.status === "archived");

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
        <CircularProgress size={24} />
      </div>
    );
  }

  return (
    <>
      <div className={classes.toolbar}>
        {hasArchived ? (
          <label className={classes.toggleLabel}>
            <Checkbox
              size="small"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              style={{ padding: 4 }}
            />
            {i18n.t("triage.manager.showArchived")}
          </label>
        ) : (
          <span />
        )}
        <button type="button" className={classes.btnPrimary} onClick={onNew}>
          <Plus /> {i18n.t("triage.manager.new")}
        </button>
      </div>

      {visible.length === 0 ? (
        <div className={classes.empty}>
          <div className={classes.emptyTitle}>{i18n.t("triage.manager.empty")}</div>
          {i18n.t("triage.manager.emptyHint")}
        </div>
      ) : (
        <div className={classes.rows}>
          {visible.map((def) => {
            const archived = def.status === "archived";
            return (
              <div key={def.id} className={classes.row}>
                <div
                  className={classes.rowMain}
                  onClick={() => !archived && onEdit(def.id)}
                  onKeyDown={(ev) => {
                    if (!archived && (ev.key === "Enter" || ev.key === " ")) {
                      ev.preventDefault();
                      onEdit(def.id);
                    }
                  }}
                  role="button"
                  tabIndex={archived ? -1 : 0}
                >
                  <div className={classes.rowTitle}>
                    {def.name}
                    <span className={`${classes.chip} ${def.status === "draft" ? classes.chipOutline : ""}`}>
                      {i18n.t(`triage.manager.status.${def.status}`)}
                    </span>
                  </div>
                  {def.description && <div className={classes.rowDesc}>{def.description}</div>}
                  <div className={classes.rowMeta}>
                    {def.currentVersion
                      ? i18n.t("triage.manager.publishedVersion", { version: def.currentVersion.version })
                      : i18n.t("triage.manager.notPublished")}
                    {def.activeSessions > 0 && ` · ${i18n.t("triage.manager.activeSessions", { count: def.activeSessions })}`}
                    {` · ${i18n.t("triage.manager.updatedAt", { date: formatDate(def.updatedAt) })}`}
                  </div>
                </div>
                <div className={classes.rowActions}>
                  {busyId === def.id && <CircularProgress size={16} style={{ marginRight: 6 }} />}
                  {!archived && (
                    <Tooltip title={i18n.t("triage.manager.actions.edit")}>
                      <span>
                        <button type="button" className={classes.iconBtn} onClick={() => onEdit(def.id)} disabled={!!busyId} aria-label={i18n.t("triage.manager.actions.edit")}>
                          <Pencil />
                        </button>
                      </span>
                    </Tooltip>
                  )}
                  <Tooltip title={i18n.t("triage.manager.actions.duplicate")}>
                    <span>
                      <button type="button" className={classes.iconBtn} onClick={() => duplicate(def)} disabled={!!busyId} aria-label={i18n.t("triage.manager.actions.duplicate")}>
                        <Copy />
                      </button>
                    </span>
                  </Tooltip>
                  {archived ? (
                    <Tooltip title={i18n.t("triage.manager.actions.restore")}>
                      <span>
                        <button type="button" className={classes.iconBtn} onClick={() => restore(def)} disabled={!!busyId} aria-label={i18n.t("triage.manager.actions.restore")}>
                          <ArchiveRestore />
                        </button>
                      </span>
                    </Tooltip>
                  ) : (
                    <Tooltip title={i18n.t("triage.manager.actions.archive")}>
                      <span>
                        <button type="button" className={classes.iconBtn} onClick={() => setArchiving(def)} disabled={!!busyId} aria-label={i18n.t("triage.manager.actions.archive")}>
                          <Archive />
                        </button>
                      </span>
                    </Tooltip>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmationModal
        title={i18n.t("triage.manager.actions.archive")}
        open={!!archiving}
        onClose={() => setArchiving(null)}
        onConfirm={archive}
        confirmLoading={!!busyId}
      >
        {archiving ? i18n.t("triage.manager.archiveConfirm", { name: archiving.name }) : ""}
      </ConfirmationModal>
    </>
  );
};

export default DefinitionList;
